import { useEffect, useRef, useState } from 'react';
import type { Holding, StockWithSeries } from '@tradekaro/shared';
import { Card, Chip, Delta, TickerValue } from '../../components/UI';
import { Icon } from '../../components/Icons';
import { DonutAllocation, Sparkline } from '../../components/Charts';
import { HOLDINGS } from './data';
import { useBreakpoint } from '../../lib/useBreakpoint';
import type { TradeRow, WalletTxn } from '../../lib/dataHooks';
import { useStockSnapshots, type ApiSnapshot } from '../../lib/hooks';
import { getMarketStatus } from '../../lib/marketHours';
import {
  addWatchlistItem,
  removeWatchlistItem,
  searchSymbols,
  useIndices,
  useWatchlists,
  type SymbolHit,
} from '../../lib/dataHooks';

/** Build a deterministic micro-sparkline from a single value + day Δ%.
 *  Used for index cards when we only have point-in-time data (DPS snapshot
 *  without per-minute history). Trends in the direction of the day Δ% so the
 *  visual matches the green/red accent. */

/** Stable color per ticker symbol — produces an oklch hue from the symbol's char codes. */
const colorForSymbol = (sym: string): string => {
  let h = 0;
  for (let i = 0; i < sym.length; i++) h = (h * 31 + sym.charCodeAt(i)) >>> 0;
  return `oklch(var(--tk-cat-l, 0.6) var(--tk-cat-c, 0.14) ${h % 360})`;
};

interface AllocationSlice {
  sym: string;
  qty: number;
  avg: number;
  color: string;
}

interface AllocationCardProps {
  /** When provided, the donut renders from these holdings (live source).
   *  When undefined, falls back to fixture HOLDINGS (non-authed / demo).
   *  When the array is empty, renders an explicit empty state. */
  holdings?: AllocationSlice[];
}

export const AllocationCard = ({ holdings }: AllocationCardProps = {}) => {
  const useFallback = holdings === undefined;
  const slices: AllocationSlice[] = useFallback
    ? HOLDINGS.map((h) => ({ sym: h.sym, qty: h.qty, avg: h.avg, color: h.color }))
    : holdings;

  if (!useFallback && slices.length === 0) {
    return <EmptyAllocationCard />;
  }

  const total = slices.reduce((s, h) => s + h.qty * h.avg, 0);
  const sortedSlices = [...slices].sort((a, b) => b.qty * b.avg - a.qty * a.avg);
  return (
    <Card padding={28} style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
          Allocation
        </h3>
        <span style={{ fontSize: 12, color: 'var(--tk-ink-3)', fontWeight: 600 }}>
          {slices.length} {slices.length === 1 ? 'holding' : 'holdings'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginTop: 18 }}>
        <div style={{ position: 'relative' }}>
          <DonutAllocation
            slices={sortedSlices.map((h) => ({ value: h.qty * h.avg, color: h.color }))}
            size={140}
            thickness={14}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: 'var(--tk-ink-3)',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Invested
            </div>
            <div
              className="mono"
              style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '-0.02em', marginTop: 2, whiteSpace: 'nowrap' }}
              title={`PKR ${total.toLocaleString('en-PK', { maximumFractionDigits: 2 })}`}
            >
              {total.toLocaleString('en-PK', { maximumFractionDigits: 0 })}
            </div>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sortedSlices.map((h) => {
            const pct = total > 0 ? ((h.qty * h.avg) / total) * 100 : 0;
            return (
              <div
                key={h.sym}
                style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 99,
                    background: h.color,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontWeight: 600 }}>{h.sym}</span>
                <span style={{ flex: 1 }} />
                <span className="mono" style={{ color: 'var(--tk-ink-3)', fontSize: 12.5 }}>
                  {pct.toFixed(1)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

const EmptyAllocationCard = () => (
  <Card padding={28} style={{ display: 'flex', flexDirection: 'column' }}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4,
      }}
    >
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
        Allocation
      </h3>
      <span style={{ fontSize: 12, color: 'var(--tk-ink-3)', fontWeight: 600 }}>0 holdings</span>
    </div>
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        marginTop: 16,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 140,
          height: 140,
          borderRadius: 999,
          border: '14px dashed var(--tk-line)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--tk-ink-3)',
        }}
      >
        <Icon name="briefcase" size={28} stroke={1.4} />
      </div>
      <div style={{ maxWidth: 260 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 700,
            color: 'var(--tk-ink)',
            marginBottom: 4,
          }}
        >
          No holdings yet
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--tk-ink-3)', lineHeight: 1.45 }}>
          Buy a stock, or import a past position from{' '}
          <strong style={{ color: 'var(--tk-ink-2)' }}>Portfolio → Quick add</strong>.
        </div>
      </div>
    </div>
  </Card>
);

interface IndicesProps {
  showSparkline?: boolean;
}

// Display names for the DPS index symbols. The wire format is the bare
// symbol (KSE100, KMI30, etc.); humans expect the dashed form.
const INDEX_DISPLAY_NAME: Record<string, string> = {
  KSE100: 'KSE-100',
  KSE30: 'KSE-30',
  KMI30: 'KMI-30',
  ALLSHR: 'ALL SHARE',
  KMIALLSHR: 'KMI ALL SHR',
};

export const IndicesStrip = ({ showSparkline = true }: IndicesProps) => {
  const bp = useBreakpoint();
  const indicesQ = useIndices(60_000);
  const live = indicesQ.data?.indices ?? [];
  // Real data only. The sparkline is today's intraday series from the feed;
  // before the first push of the day there is nothing to draw.
  const merged: Array<{ name: string; value: number; change: number; series: number[] }> = live.map((i) => ({
    name: INDEX_DISPLAY_NAME[i.sym] ?? i.sym,
    value: i.value,
    change: i.dayChangePct ?? 0,
    series: (i.series ?? []).length >= 2 ? (i.series ?? []).map((p) => p.value) : [],
  }));
  if (merged.length === 0) return null;
  const cols = bp === 'mobile' ? '1fr 1fr' : bp === 'tablet' ? 'repeat(2, 1fr)' : `repeat(${Math.min(merged.length, 5)}, 1fr)`;
  return (
  <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 20 }}>
    {merged.map((idx) => (
      <Card key={idx.name} padding={20} hover style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--tk-ink-3)',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {idx.name}
            </div>
            <div
              className="mono"
              style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', marginTop: 6 }}
            >
              {idx.value.toLocaleString('en-PK', { maximumFractionDigits: 2 })}
            </div>
            <div style={{ marginTop: 4 }}>
              <Delta value={idx.change} size="sm" subtle />
            </div>
          </div>
          {showSparkline && (
            <Sparkline data={idx.series} width={80} height={40} up={idx.change >= 0} fill={false} />
          )}
        </div>
      </Card>
    ))}
  </div>
  );
};

/** Minimal shape both fixture data and the live `/stocks/movers` response can
 *  be mapped to without losing the card's visual fidelity. */
export interface MoverRow {
  sym: string;
  /** Sector for the row sub-label. Omitted by the live API; we look it up
   *  client-side from the symbols catalogue when available, otherwise blank. */
  sector?: string;
  price: number;
  /** Day Δ % vs session open (already a percentage, e.g. 2.4 == +2.4%). */
  change: number;
}

interface MoversProps {
  gainers: MoverRow[];
  losers: MoverRow[];
  onPick?: (sym: string) => void;
  /** When the dashboard is showing "last session" data instead of live, the
   *  card title reflects that so "0% on a closed market" doesn't confuse. */
  titleSuffix?: string;
}

export const MergedMoversCard = ({ gainers, losers, onPick, titleSuffix }: MoversProps) => {
  const [tab, setTab] = useState<'gainers' | 'losers'>('gainers');
  const stocks = tab === 'gainers' ? gainers : losers;
  return (
    <Card padding={0}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px 14px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
          Movers{titleSuffix ? ` · ${titleSuffix}` : ''}
        </h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <Chip active={tab === 'gainers'} onClick={() => setTab('gainers')}>
            Gainers
          </Chip>
          <Chip active={tab === 'losers'} onClick={() => setTab('losers')}>
            Losers
          </Chip>
        </div>
      </div>
      <div>
        {stocks.map((s) => (
          <div
            key={s.sym}
            onClick={() => onPick?.(s.sym)}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              alignItems: 'center',
              gap: 12,
              padding: '11px 24px',
              borderTop: '1px solid var(--tk-line-2)',
              cursor: 'pointer',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{s.sym}</div>
              <div style={{ fontSize: 11, color: 'var(--tk-ink-3)', marginTop: 2 }}>{s.sector}</div>
            </div>
            <div
              className="mono"
              style={{ fontSize: 12.5, color: 'var(--tk-ink-2)', textAlign: 'right' }}
            >
              {s.price.toLocaleString('en-PK', { maximumFractionDigits: 2 })}
            </div>
            <Delta value={s.change} size="sm" />
          </div>
        ))}
      </div>
    </Card>
  );
};

interface WatchlistProps {
  /** Fallback stocks to render when the user is signed out / API offline.
   *  When the API responds (even with an empty list), this is ignored. */
  stocks: StockWithSeries[];
  /** Note: kept for API compatibility but not used now that the live card
   *  doesn't show sparklines (we lack per-symbol intraday series). */
  showSparkline?: boolean;
  onPick?: (sym: string) => void;
}

export const WatchlistCard = ({ stocks, onPick }: WatchlistProps) => {
  const wlQ = useWatchlists(30_000);
  const apiAvailable = wlQ.data !== null;
  // Default list = first watchlist (server auto-creates one on first GET).
  const list = wlQ.data?.watchlists[0];
  const liveSyms = list?.items ?? [];
  // Use the API's symbol list when authed; fall back to fixtures otherwise.
  const symsToShow = apiAvailable ? liveSyms : stocks.map((s) => s.sym);
  const snapshots = useStockSnapshots(symsToShow, 5000);
  const marketOpen = getMarketStatus() === 'open';
  const hasLive = apiAvailable && snapshots.size > 0;
  const showLive = hasLive && marketOpen;
  const badgeLabel = !apiAvailable ? 'Demo' : marketOpen ? 'Live' : 'Market closed';
  const badgeTitle = !apiAvailable
    ? 'Fixtures (signed out)'
    : marketOpen
      ? 'Live AHL feed'
      : 'PSX is closed — last snapshot shown';

  const [adding, setAdding] = useState(false);

  const refresh = wlQ.refetch;
  const onRemove = async (sym: string) => {
    if (!list) return;
    try {
      await removeWatchlistItem(list.id, sym);
      await refresh();
    } catch {
      // ignore — refresh on next interval will reconcile
    }
  };
  const onAdd = async (sym: string) => {
    if (!list) return;
    try {
      await addWatchlistItem(list.id, sym);
      await refresh();
    } catch {
      // ignore
    }
  };

  return (
  <Card padding={0}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px 16px',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
        Your watchlist
      </h3>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 11,
            color: showLive ? 'var(--tk-up)' : 'var(--tk-ink-3)',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontFamily: 'var(--tk-mono)',
          }}
          title={badgeTitle}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              background: showLive ? 'var(--tk-up)' : 'var(--tk-ink-3)',
              animation: showLive ? 'tk-pulse 1.6s var(--tk-ease) infinite' : undefined,
            }}
          />
          {badgeLabel}
        </span>
        {apiAvailable && list && (
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            title={adding ? 'Close' : 'Add symbol'}
            aria-label={adding ? 'Close add symbol' : 'Add symbol'}
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              border: '1px solid var(--tk-line)',
              background: adding ? 'var(--tk-accent)' : 'var(--tk-surface)',
              color: adding ? 'var(--tk-accent-ink)' : 'var(--tk-ink)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s var(--tk-ease)',
            }}
            onMouseEnter={(e) => {
              if (!adding) e.currentTarget.style.borderColor = 'var(--tk-accent)';
            }}
            onMouseLeave={(e) => {
              if (!adding) e.currentTarget.style.borderColor = 'var(--tk-line)';
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              style={{
                transform: adding ? 'rotate(45deg)' : 'rotate(0deg)',
                transition: 'transform 0.18s var(--tk-ease)',
              }}
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        )}
      </div>
    </div>
    {apiAvailable && list && adding && (
      <SymbolSearchPanel
        existing={new Set(symsToShow)}
        onPick={async (sym) => {
          await onAdd(sym);
          setAdding(false);
        }}
        onClose={() => setAdding(false)}
      />
    )}
    <div>
      {apiAvailable && symsToShow.length === 0 && !adding && (
        <div
          style={{
            padding: '20px 24px',
            fontSize: 13,
            color: 'var(--tk-ink-3)',
            borderTop: '1px solid var(--tk-line-2)',
          }}
        >
          Your watchlist is empty — tap <strong style={{ color: 'var(--tk-ink)' }}>+</strong> above to add a symbol.
        </div>
      )}
      {!apiAvailable &&
        stocks.map((s) => {
          const snap = snapshots.get(s.sym);
          const price = snap?.last ?? s.price;
          const change =
            snap?.last !== undefined && snap?.open !== undefined && snap.open > 0
              ? ((snap.last - snap.open) / snap.open) * 100
              : s.change;
          return (
            <WatchlistRow
              key={s.sym}
              sym={s.sym}
              name={s.name}
              price={price}
              change={change}
              onPick={onPick}
            />
          );
        })}
      {apiAvailable &&
        symsToShow.map((sym) => {
          const snap = snapshots.get(sym);
          const price = snap?.last;
          const change =
            snap?.last !== undefined && snap?.open !== undefined && snap.open > 0
              ? ((snap.last - snap.open) / snap.open) * 100
              : undefined;
          return (
            <WatchlistRow
              key={sym}
              sym={sym}
              price={price}
              change={change}
              onPick={onPick}
              onRemove={() => void onRemove(sym)}
            />
          );
        })}
    </div>
  </Card>
  );
};

interface WatchlistRowProps {
  sym: string;
  name?: string;
  price?: number;
  change?: number;
  onPick?: (sym: string) => void;
  onRemove?: () => void;
}

const WatchlistRow = ({ sym, name, price, change, onPick, onRemove }: WatchlistRowProps) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '1fr 110px 100px 30px',
      alignItems: 'center',
      gap: 12,
      padding: '12px 24px',
      borderTop: '1px solid var(--tk-line-2)',
      transition: 'background 0.15s var(--tk-ease)',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--tk-surface-2)')}
    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
  >
    <div onClick={() => onPick?.(sym)} style={{ cursor: 'pointer' }}>
      <div style={{ fontWeight: 700, fontSize: 13.5, letterSpacing: '0.005em' }}>{sym}</div>
      {name && (
        <div style={{ fontSize: 11.5, color: 'var(--tk-ink-3)', marginTop: 2 }}>{name}</div>
      )}
    </div>
    <div
      className="mono"
      onClick={() => onPick?.(sym)}
      style={{ fontSize: 13.5, fontWeight: 500, textAlign: 'right', cursor: 'pointer' }}
    >
      {price !== undefined ? <TickerValue value={price} decimals={2} /> : '—'}
    </div>
    <div
      onClick={() => onPick?.(sym)}
      style={{ textAlign: 'right', cursor: 'pointer' }}
    >
      {change !== undefined ? <Delta value={change} size="sm" /> : <span style={{ color: 'var(--tk-ink-3)', fontSize: 12 }}>—</span>}
    </div>
    {onRemove ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        title={`Remove ${sym}`}
        aria-label={`Remove ${sym} from watchlist`}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--tk-ink-3)',
          padding: 4,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.12s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'var(--tk-down)';
          e.currentTarget.style.background = 'var(--tk-down-soft)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--tk-ink-3)';
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M6 6l12 12M18 6l-12 12" />
        </svg>
      </button>
    ) : (
      <span />
    )}
  </div>
);

interface SymbolSearchPanelProps {
  onPick: (sym: string) => Promise<void>;
  onClose: () => void;
  existing: Set<string>;
}

/** Inline search panel rendered between the watchlist header and the rows
 *  while the header `+` button is in its open state. Auto-focuses the input,
 *  Esc closes, Enter picks the first match. */
const SymbolSearchPanel = ({ onPick, onClose, existing }: SymbolSearchPanelProps) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SymbolHit[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const trimmed = q.trim();
    if (!trimmed) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await searchSymbols(trimmed, 8);
        if (!cancelled) setResults(r.results);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  const submit = async (sym: string) => {
    if (existing.has(sym.toUpperCase())) return;
    await onPick(sym.toUpperCase());
  };

  return (
    <div
      style={{
        borderTop: '1px solid var(--tk-line-2)',
        padding: '12px 16px 14px',
        background: 'var(--tk-surface-2)',
      }}
    >
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          if (e.key === 'Enter' && results[0]) void submit(results[0].sym);
        }}
        placeholder="Search PSX (e.g. ENGRO, OGDC)"
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: 13,
          border: '1px solid var(--tk-line)',
          borderRadius: 'var(--tk-radius-sm)',
          background: 'var(--tk-surface)',
          color: 'var(--tk-ink)',
          outline: 'none',
          fontFamily: 'var(--tk-sans)',
        }}
      />
      <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto' }}>
        {loading && <div style={{ fontSize: 12, color: 'var(--tk-ink-3)', padding: 8 }}>Searching…</div>}
        {!loading && q.trim() && results.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--tk-ink-3)', padding: 8 }}>No matches.</div>
        )}
        {results.map((r) => {
          const already = existing.has(r.sym.toUpperCase());
          return (
            <button
              key={r.sym}
              type="button"
              disabled={already}
              onClick={() => void submit(r.sym)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                width: '100%',
                padding: '8px 10px',
                background: 'transparent',
                border: 'none',
                cursor: already ? 'not-allowed' : 'pointer',
                color: already ? 'var(--tk-ink-3)' : 'var(--tk-ink)',
                fontSize: 13,
                textAlign: 'left',
                borderRadius: 6,
                transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => {
                if (!already) e.currentTarget.style.background = 'var(--tk-surface)';
              }}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span>
                <strong style={{ fontWeight: 700 }}>{r.sym}</strong>
                <span style={{ color: 'var(--tk-ink-3)', marginLeft: 8, fontSize: 12 }}>{r.name}</span>
              </span>
              {already && <span style={{ fontSize: 11, color: 'var(--tk-ink-3)' }}>added</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

interface MoverListProps {
  title: string;
  stocks: MoverRow[];
  onPick?: (sym: string) => void;
}

export const MoversCard = ({ title, stocks, onPick }: MoverListProps) => (
  <Card padding={0}>
    <div style={{ padding: '20px 22px 14px' }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</h3>
    </div>
    <div>
      {stocks.length === 0 && (
        <div style={{ padding: '14px 22px 18px', fontSize: 12.5, color: 'var(--tk-ink-3)', borderTop: '1px solid var(--tk-line-2)' }}>
          No market data yet for this session.
        </div>
      )}
      {stocks.map((s) => (
        <div
          key={s.sym}
          onClick={() => onPick?.(s.sym)}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            alignItems: 'center',
            gap: 12,
            padding: '11px 22px',
            borderTop: '1px solid var(--tk-line-2)',
            cursor: 'pointer',
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{s.sym}</div>
            <div style={{ fontSize: 11, color: 'var(--tk-ink-3)', marginTop: 2 }}>{s.sector}</div>
          </div>
          <div className="mono" style={{ fontSize: 12.5, color: 'var(--tk-ink-2)', textAlign: 'right' }}>
            {s.price.toLocaleString('en-PK', { maximumFractionDigits: 2 })}
          </div>
          <Delta value={s.change} size="sm" />
        </div>
      ))}
    </div>
  </Card>
);

export const NewsCard = () => (
  <Card padding={0}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px 14px',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
        News & insights
      </h3>
    </div>
    <div style={{ padding: '4px 24px 22px', fontSize: 12.5, color: 'var(--tk-ink-3)', lineHeight: 1.5, borderTop: '1px solid var(--tk-line-2)' }}>
      <div style={{ paddingTop: 14 }}>
        No news source is connected yet. The market feed carries regulatory alerts only; editorial news
        needs a licensed provider.
      </div>
    </div>
  </Card>
);

interface ActivityProps {
  trades: TradeRow[];
  txns: WalletTxn[];
}

interface ActivityRow {
  key: string;
  type: 'BUY' | 'SELL' | 'DEPOSIT' | 'WITHDRAW' | 'DIVIDEND';
  sym?: string;
  qty?: number;
  price?: number;
  amount?: number;
  method?: string | null;
  at: number;
}

const relTime = (ms: number): string => {
  const d = new Date(ms);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Karachi' });
  if (sameDay) return time;
  const yest = new Date(today.getTime() - 86_400_000);
  if (d.toDateString() === yest.toDateString()) return `Yesterday ${time}`;
  return d.toLocaleDateString('en-PK', { day: 'numeric', month: 'short', timeZone: 'Asia/Karachi' });
};

export const ActivityCard = ({ trades, txns }: ActivityProps) => {
  const rows: ActivityRow[] = [
    ...trades.map((t) => ({
      key: `t-${t.id}`,
      type: t.side,
      sym: t.symbol,
      qty: t.qty,
      price: Number(t.price),
      at: new Date(t.executedAt).getTime(),
    })),
    ...txns
      .filter((x) => x.type === 'DEPOSIT' || x.type === 'WITHDRAW' || x.type === 'DIVIDEND')
      .map((x) => ({
        key: `c-${x.id}`,
        type: x.type as 'DEPOSIT' | 'WITHDRAW' | 'DIVIDEND',
        amount: Math.abs(Number(x.amount)),
        method: x.method,
        sym: x.counterparty ?? undefined,
        at: new Date(x.occurredAt).getTime(),
      })),
  ]
    .sort((a, b) => b.at - a.at)
    .slice(0, 6);
  const iconMap = { BUY: 'arrowDown', SELL: 'arrowUp', DEPOSIT: 'deposit', WITHDRAW: 'withdraw', DIVIDEND: 'star' } as const;
  const colorMap: Record<string, string> = {
    BUY: 'var(--tk-up)',
    SELL: 'var(--tk-down)',
    DEPOSIT: 'var(--tk-ink-2)',
    WITHDRAW: 'var(--tk-ink-2)',
    DIVIDEND: 'var(--tk-warn)',
  };
  return (
    <Card padding={0}>
      <div style={{ padding: '20px 24px 14px' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
          Recent activity
        </h3>
      </div>
      <div>
        {rows.length === 0 && (
          <div style={{ padding: '14px 24px 20px', fontSize: 12.5, color: 'var(--tk-ink-3)', borderTop: '1px solid var(--tk-line-2)' }}>
            No trades or cash movements yet.
          </div>
        )}
        {rows.map((a) => {
          const isTrade = a.type === 'BUY' || a.type === 'SELL';
          return (
            <div
              key={a.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '13px 24px',
                borderTop: '1px solid var(--tk-line-2)',
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 99,
                  background: 'var(--tk-surface-2)',
                  border: '1px solid var(--tk-line)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colorMap[a.type],
                  flexShrink: 0,
                }}
              >
                <Icon name={iconMap[a.type]} size={14} stroke={2.2} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {a.type === 'BUY' && `Bought ${a.qty?.toLocaleString('en-PK')} ${a.sym}`}
                  {a.type === 'SELL' && `Sold ${a.qty?.toLocaleString('en-PK')} ${a.sym}`}
                  {a.type === 'DEPOSIT' && `Deposit${a.method ? ` · ${a.method}` : ''}`}
                  {a.type === 'WITHDRAW' && `Withdrawal${a.method ? ` · ${a.method}` : ''}`}
                  {a.type === 'DIVIDEND' && `Dividend${a.sym ? ` · ${a.sym}` : ''}`}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--tk-ink-3)', marginTop: 2 }}>{relTime(a.at)}</div>
              </div>
              <div className="mono" style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>
                {isTrade && a.price !== undefined && a.qty !== undefined && (
                  <>
                    <span>@ {a.price.toFixed(2)}</span>
                    <div style={{ fontSize: 11.5, color: 'var(--tk-ink-3)', fontWeight: 500, marginTop: 1 }}>
                      {(a.qty * a.price).toLocaleString('en-PK', { maximumFractionDigits: 0 })}
                    </div>
                  </>
                )}
                {!isTrade && a.amount !== undefined && (
                  <span style={{ color: a.type === 'WITHDRAW' ? 'var(--tk-down)' : 'var(--tk-up)' }}>
                    {a.type === 'WITHDRAW' ? '−' : '+'}
                    {a.amount.toLocaleString('en-PK', { maximumFractionDigits: 0 })}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
